import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useTestRide } from "./TestRideContext";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Buy" },
  { to: "/rent", label: "Rent" },
  { to: "/service", label: "Service" },
  { to: "/stores", label: "Stores" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openModal } = useTestRide();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200 shadow-[0_1px_0_rgba(11,19,32,0.04)]"
          : "bg-white/0 border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Logo height={36} />

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-semibold tracking-wide transition-colors ${
                  active ? "text-[#2A8BFF]" : "text-slate-700 hover:text-slate-900"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/contact" className="btn-outline" data-testid="nav-contact">
            Contact
          </Link>
          <button
            type="button"
            className="btn-primary"
            data-testid="nav-book-test-ride"
            onClick={() => openModal()}
          >
            Book Test Ride
          </button>
        </div>

        <button
          type="button"
          className="lg:hidden text-slate-900 p-2"
          aria-label="Open menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden bg-white border-t border-slate-200"
          data-testid="mobile-menu"
        >
          <div className="container-x py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="py-3 text-slate-900 text-base font-semibold border-b border-slate-100"
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
            <Link to="/contact" className="py-3 text-slate-900 text-base font-semibold">
              Contact
            </Link>
            <button
              type="button"
              onClick={() => {
                openModal();
                setOpen(false);
              }}
              className="btn-primary mt-3 w-full"
              data-testid="mobile-book-test-ride"
            >
              Book Test Ride
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
